import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import type { FC } from "react";
import { useMatch, useNavigate, useParams } from "react-router";
import { useAllMinimalPlaybooksQuery } from "../api/entities/playbook.js";
import { useUpdateTeammateMutation } from "../api/entities/teammate.js";
import { appPaths } from "../app/appPaths.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { useAppSelector } from "../hooks/hook.js";

export interface PerformancePlaybookSwitcherProps {
  staysOnFullPath?: boolean;
  /**
   * When provided, restricts the dropdown to this subset of playbooks (the
   * currently-selected playbook is always kept so the trigger reflects the
   * URL). Used by the teammate profile to offer only the playbooks the
   * teammate has calls on. Omit to offer every playbook.
   */
  selectablePlaybookIds?: readonly PlaybookId[];
}

export const PerformancePlaybookSwitcher: FC<
  PerformancePlaybookSwitcherProps
> = (props) => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId URL param");
  }

  const playbookId = asPlaybookId(playbookIdParam);

  const navigate = useNavigate();
  const playbookTabMatch = useMatch(`${appPaths.playbook.pattern}/:tab/*`);

  const { data: minimalPlaybooks } = useAllMinimalPlaybooksQuery({
    shouldExcludeTeammateAgentPlaybooks: true,
  });

  const teammate = useAppSelector(selectTeammate);
  const [updateTeammate] = useUpdateTeammateMutation();

  // Optional caller-supplied allowlist (e.g. the playbooks a teammate has
  // calls on). The currently-selected playbook is always kept regardless, so
  // the trigger renders correctly and the unknown-playbook detection below
  // stays keyed off the full known set rather than the narrowed subset.
  const selectablePlaybookIdSet = props.selectablePlaybookIds
    ? new Set<PlaybookId>(props.selectablePlaybookIds)
    : undefined;

  // Build the playbook dropdown options
  const knownPlaybookOptions: readonly SimpleSelectOption<PlaybookId>[] =
    minimalPlaybooks
      ?.filter(
        (p) =>
          selectablePlaybookIdSet === undefined ||
          selectablePlaybookIdSet.has(p.id) ||
          p.id === playbookId,
      )
      .map((p) => ({ label: p.name, value: p.id })) ?? [];

  // If the playbook id from the URL doesn't resolve to a known playbook,
  // surface a synthetic "Unknown playbook" option so the dropdown can still
  // reflect the current selection (and flag it as invalid below).
  const isUnknownPlaybook =
    minimalPlaybooks !== undefined &&
    !knownPlaybookOptions.some((o) => o.value === playbookId);
  const playbookOptions: readonly SimpleSelectOption<PlaybookId>[] =
    isUnknownPlaybook
      ? [
          ...knownPlaybookOptions,
          { label: "Unknown playbook", value: playbookId },
        ]
      : knownPlaybookOptions;

  // Current selected playbook is the one from the path
  const selectedPlaybookOption = playbookOptions.find(
    (o) => o.value === playbookId,
  )?.value;

  const onPlaybookChange = (value: PlaybookId) => {
    let to = appPaths.playbook.build({ playbookId: value });

    if (playbookTabMatch) {
      to += `/${playbookTabMatch.params.tab}`;

      if (props.staysOnFullPath && playbookTabMatch.params["*"]) {
        to += `/${playbookTabMatch.params["*"]}`;
      }
    }

    navigate(to, { replace: true });

    // Persist the selection so it is restored on the teammate's next visit.
    // Best effort — a failed save just falls back to the default playbook.
    if (teammate) {
      updateTeammate({
        id: teammate.id,
        patchPayload: {
          preferences: { team_performance_playbook_id: value },
        },
      });
    }
  };

  return playbookOptions.length > 0 ? (
    <SimpleSelect
      label="Playbook"
      isLabelHidden
      isInvalid={isUnknownPlaybook}
      value={selectedPlaybookOption ?? null}
      options={playbookOptions}
      onChange={onPlaybookChange}
      size={Size.SMALL}
    />
  ) : null;
};
